/**
 * YofiPlace Subscription System
 * 
 * IMPORTANT: Subscriptions are ONLY for business owners (masters/salons).
 * End-users (clients) use the platform completely FREE of charge.
 * 
 * Plans (decision 2026-07-28):
 * - pro: 99₪/month — single all-inclusive plan. Matches the market leader
 *        (QuickTor, 99₪ all-inclusive) so price can never be an objection.
 *        Includes unlimited WhatsApp, catalog placement, portfolio, AI assistant.
 *
 * The old `basic` 49₪ tier is RETIRED — it undercut the market by half, read as
 * "cheap" and did not cover WhatsApp costs. The PlanId type still allows 'basic'
 * so legacy records keep resolving, but it is no longer offered.
 * - salon4: 149₪/month — a salon with up to 4 masters.
 * - salon8: 199₪/month — a salon with up to 8 masters.
 *   A salon pays once for the whole team, not per chair: the per-master price
 *   works out lower the bigger the team, which is the point.
 *
 * Every price here is a DEFAULT. The live values come from
 * `system_config/pricing` and are editable by an admin (see usePricing), so an
 * offer can change without a deploy.
 *
 * Every new business gets 3 months (90 days) of free Pro access
 * from the date they registered (exact day, not calendar month).
 * 
 * After trial, features remain available but user sees prompts to subscribe.
 * Actual payments will be configured via YaadPay integration later.
 */

export type PlanId = 'free' | 'basic' | 'pro' | 'salon4' | 'salon8';

export interface PlanDef {
  id: PlanId;
  price: number;        // monthly price in ₪ (default; admin can override)
  features: string[];   // i18n keys for feature list
  highlighted?: boolean; // UI emphasis
  /** Salon plans only: how many masters the price covers. */
  maxMasters?: number;
}

export const PLANS: PlanDef[] = [
  {
    id: 'pro',
    price: 99,
    features: [
      'subscription.feat_profile',
      'subscription.feat_calendar',
      'subscription.feat_services',
      'subscription.feat_requests',
      'subscription.feat_analytics',
      'subscription.feat_clients',
      'subscription.feat_notifications',
      'subscription.feat_reviews',
      'subscription.feat_whatsapp_full',
      'subscription.feat_whatsapp_booking',
      'subscription.feat_whatsapp_reminders',
      'subscription.feat_priority_catalog',
      'subscription.feat_boost_ads',
      'subscription.feat_blog',
      'subscription.feat_portfolio',
      'subscription.feat_packages',
      'subscription.feat_ai_assistant',
    ],
    highlighted: true,
  },
  {
    id: 'salon4',
    price: 149,
    maxMasters: 4,
    features: [
      'subscription.feat_salon_team',
      'subscription.feat_salon_calendar',
      'subscription.feat_salon_move',
      'subscription.feat_everything_pro',
    ],
  },
  {
    id: 'salon8',
    price: 199,
    maxMasters: 8,
    features: [
      'subscription.feat_salon_team',
      'subscription.feat_salon_calendar',
      'subscription.feat_salon_move',
      'subscription.feat_everything_pro',
    ],
  },
];

/** Plans offered to a salon, cheapest first. */
export const SALON_PLAN_IDS: PlanId[] = ['salon4', 'salon8'];

/** Live prices, merged over the defaults above. */
export type PriceOverrides = Partial<Record<PlanId, number>>;

/**
 * Clamp an admin-entered price. A negative or absurd number would be shown to
 * every visitor, so anything outside a sane band falls back to the default.
 */
export function normalizePrice(value: unknown, fallback: number): number {
  if (typeof value !== 'number' && typeof value !== 'string') return fallback;
  if (typeof value === 'string' && value.trim() === '') return fallback;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0 || n > 100000) return fallback;
  return Math.round(n);
}

/** The plan list with any admin overrides applied. */
export function plansWithPrices(overrides?: PriceOverrides): PlanDef[] {
  if (!overrides) return PLANS;
  return PLANS.map(p => ({ ...p, price: normalizePrice(overrides[p.id], p.price) }));
}

/** Which salon plan fits a team of this size (the largest is the fallback). */
export function salonPlanForTeam(teamSize: number, plans: PlanDef[] = PLANS): PlanDef | null {
  const salonPlans = plans.filter(p => SALON_PLAN_IDS.includes(p.id))
    .sort((a, b) => (a.maxMasters || 0) - (b.maxMasters || 0));
  if (salonPlans.length === 0) return null;
  return salonPlans.find(p => teamSize <= (p.maxMasters || 0)) || salonPlans[salonPlans.length - 1];
}

/**
 * DEFAULT trial duration in days (3 months — правило фирмы, K-0049: обещание
 * напечатано на 500 визитках, менять нельзя). This is only the fallback:
 * the live value is configurable by an admin in `system_config/trial.days`
 * (read via `useTrialConfig()`), and an individual master can get extra free
 * days via `businesses/{uid}.trialBonusDays`.
 * Every helper below takes optional `trialDays` / `bonusDays` arguments and
 * falls back to this constant, so existing call sites keep working unchanged.
 */
export const TRIAL_DAYS = 90;
/** Hard bounds so a bad admin value can never brick access. */
export const TRIAL_DAYS_MIN = 0;
export const TRIAL_DAYS_MAX = 730;
/** Clamp a configured trial length to a sane range. */
export function normalizeTrialDays(days: unknown): number {
  // Only a number or a numeric string is a real configured value. Without this
  // guard `Number(null)`, `Number('')`, `Number([])` and `Number(false)` all
  // coerce to 0, which would silently end the trial for EVERY master — the one
  // thing the bounds below exist to prevent. `useTrialConfig` only checks for
  // `undefined`, so a null `days` field would otherwise reach here.
  // An admin explicitly saving 0 is still honoured: 0 is a number.
  if (typeof days !== 'number' && typeof days !== 'string') return TRIAL_DAYS;
  if (typeof days === 'string' && days.trim() === '') return TRIAL_DAYS;
  const n = Number(days);
  if (!Number.isFinite(n)) return TRIAL_DAYS;
  return Math.min(TRIAL_DAYS_MAX, Math.max(TRIAL_DAYS_MIN, Math.round(n)));
}

/**
 * Calculate trial end date from a registration timestamp.
 * Registration date can be a Firestore Timestamp, Date, or ISO string.
 */
export function getTrialEndDate(
  registeredAt: Date | string | { toDate: () => Date } | null | undefined,
  trialDays: number = TRIAL_DAYS,
  bonusDays: number = 0,
): Date | null {
  if (!registeredAt) return null;

  let regDate: Date;
  if (registeredAt instanceof Date) {
    regDate = registeredAt;
  } else if (typeof registeredAt === 'string') {
    regDate = new Date(registeredAt);
  } else if (typeof (registeredAt as { toDate: () => Date }).toDate === 'function') {
    regDate = (registeredAt as { toDate: () => Date }).toDate();
  } else {
    return null;
  }

  if (isNaN(regDate.getTime())) return null;

  const trialEnd = new Date(regDate);
  const base = normalizeTrialDays(trialDays);
  const bonus = Math.max(0, Math.round(Number(bonusDays) || 0));
  trialEnd.setDate(trialEnd.getDate() + base + bonus);
  return trialEnd;
}

/**
 * Get the number of trial days remaining.
 * Returns 0 if trial is expired or no registration date.
 */
export function getTrialDaysRemaining(
  registeredAt: Date | string | { toDate: () => Date } | null | undefined,
  trialDays: number = TRIAL_DAYS,
  bonusDays: number = 0,
): number {
  const end = getTrialEndDate(registeredAt, trialDays, bonusDays);
  if (!end) return 0;

  const now = new Date();
  const diff = end.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

/**
 * Check if the trial period is still active.
 */
export function isTrialActive(
  registeredAt: Date | string | { toDate: () => Date } | null | undefined,
  trialDays: number = TRIAL_DAYS,
  bonusDays: number = 0,
): boolean {
  return getTrialDaysRemaining(registeredAt, trialDays, bonusDays) > 0;
}

/**
 * Determine the effective plan for a business.
 * During trial: 'pro' (all features).
 * After trial: whatever plan is stored (defaults to 'free' → prompts).
 */
export function getEffectivePlan(
  registeredAt: Date | string | { toDate: () => Date } | null | undefined,
  storedPlan?: PlanId,
  trialDays: number = TRIAL_DAYS,
  bonusDays: number = 0,
): PlanId {
  if (isTrialActive(registeredAt, trialDays, bonusDays)) return 'pro';
  return storedPlan || 'free';
}

/**
 * Format a date for display, respecting locale.
 */
export function formatDate(date: Date | null, locale: string = 'he-IL'): string {
  if (!date) return '—';
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// ─── Cancellation ────────────────────────────────────────────────────────────
/**
 * A continuous transaction under Israeli consumer law has to be cancellable by
 * the same means it was entered into, at any time, without the customer having
 * to ask a human. The master signs up inside the cabinet, so the cabinet is
 * where the cancel button lives — see /dashboard/subscription.
 *
 * Cancelling never cuts service off mid-period: the master keeps what they have
 * already paid for (or the rest of the trial), and simply is not charged again.
 * That is what `accessEndsAt` below expresses.
 */
export type SubscriptionStatus = 'trial' | 'active' | 'cancelled' | 'expired';

export interface SubscriptionState {
  plan?: PlanId;
  status?: SubscriptionStatus;
  startDate?: Date | string | { toDate: () => Date };
  endDate?: Date | string | { toDate: () => Date };
  /** false once cancelled — the flag a payment provider must honour. */
  autoRenew?: boolean;
  cancelledAt?: Date | string | { toDate: () => Date };
  /** Optional and never required: asking why must not become an obstacle. */
  cancelReason?: string;
}

/** Read a Firestore timestamp / ISO string / Date into a Date. */
export function toDate(
  value: Date | string | { toDate: () => Date } | null | undefined,
): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof (value as { toDate: () => Date }).toDate === 'function') {
    const d = (value as { toDate: () => Date }).toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function isCancelled(sub: SubscriptionState | null | undefined): boolean {
  return sub?.status === 'cancelled' || sub?.autoRenew === false;
}

/**
 * What a master keeps after cancelling, and whether anything is still owed.
 *
 * Paid period first: it is already paid for. Otherwise the trial runs to its
 * own end. Either way `willBeCharged` is false — that is the whole point of
 * cancelling, and it is the sentence the confirmation screen has to be able to
 * state plainly.
 */
export function cancellationEffect(opts: {
  sub?: SubscriptionState | null;
  trialEnd?: Date | null;
  now?: Date;
}): { accessEndsAt: Date | null; willBeCharged: false; duringTrial: boolean } {
  const now = opts.now || new Date();
  const paidUntil = toDate(opts.sub?.endDate);
  const trialEnd = opts.trialEnd || null;
  const trialStillRunning = !!trialEnd && trialEnd.getTime() > now.getTime();

  if (paidUntil && paidUntil.getTime() > now.getTime()) {
    return { accessEndsAt: paidUntil, willBeCharged: false, duringTrial: false };
  }
  return {
    accessEndsAt: trialStillRunning ? trialEnd : null,
    willBeCharged: false,
    duringTrial: trialStillRunning,
  };
}

/**
 * When the first charge would fall — the moment the law wants the customer
 * warned about in advance. A trial that has already been converted to a paid
 * plan has no "first charge" left to announce.
 */
export function firstChargeDate(opts: {
  sub?: SubscriptionState | null;
  trialEnd?: Date | null;
}): Date | null {
  if (opts.sub?.status === 'active') return null;
  if (isCancelled(opts.sub)) return null;
  return opts.trialEnd || null;
}

/** How many days before the first charge the master must be warned. */
export const PRE_CHARGE_NOTICE_DAYS = 3;
